/** Notifications and incidents: acknowledgement releases nothing; external routes are independent (U093, U094). */
import { decodeIncidentsRow, type IncidentDetail, type IncidentsRow } from "../../contracts/ops.ts";
import { badge, definitions, instantCell } from "../../app/components.ts";
import { h, type Child } from "../../app/dom.ts";
import { acknowledge } from "../../app/latches.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { byKindOf, healthTone, mountOps, panel } from "../ops-common.ts";

type Route = Extract<IncidentsRow, { kind: "route" }>;

const ROUTE_LABEL: Readonly<Record<Route["routeKind"], string>> = {
  APPLICATION: "Application notification (shares the application's fate)",
  EXTERNAL_HEARTBEAT: "External heartbeat (independent of the application)",
  EXTERNAL_ALERT: "External outage alert (independent of the application)",
};

function incident(ctx: ViewContext, row: IncidentDetail): HTMLElement {
  const already = row.acknowledged || ctx.acknowledgements.get().has(row.incidentId);
  const seen = h("span", { attrs: { "data-acknowledged": "" } }, already ? "yes" : "no");
  const button = h(
    "button",
    {
      class: "ack",
      attrs: {
        type: "button",
        "aria-label": `Acknowledge incident ${row.incidentId} (does not release the safety latch)`,
      },
      on: {
        click: () => {
          acknowledge(ctx.acknowledgements, row.incidentId);
          seen.textContent = "yes";
          button.textContent = "Acknowledged";
        },
      },
    },
    already ? "Acknowledged" : "Acknowledge",
  );
  const severityTone = row.severity === "LOW" || row.severity === "MEDIUM" ? "warn" : "bad";
  return h(
    "article",
    {
      class: "panel",
      attrs: { "data-incident": row.incidentId, "aria-labelledby": `inc-${row.incidentId}` },
    },
    h("h2", { attrs: { id: `inc-${row.incidentId}` } }, `${row.incidentId}: ${row.title}`),
    h("p", {}, badge(row.severity, severityTone), " ", badge(row.status, "info")),
    definitions([
      ["Scope", row.scope],
      ["Owner", row.owner ?? "unassigned"],
      ["Onset", instantCell(row.onset)],
      ["Acknowledged", seen],
      ["Snoozed until", instantCell(row.snoozedUntil)],
    ]),
    h(
      "p",
      { class: "latch", attrs: { "data-latch": row.safetyLatch } },
      `Safety latch: ${row.safetyLatch}. Acknowledging or closing an incident does not release it.`,
    ),
    button,
    h("h3", {}, "Timeline"),
    h(
      "ol",
      { class: "timeline", attrs: { "aria-label": `Timeline of ${row.incidentId}` } },
      ...row.timeline.map((entry) => h("li", {}, instantCell(entry.at), ` ${entry.text}`)),
    ),
    h("h3", {}, "Escalation history"),
    row.escalations.length === 0
      ? h("p", {}, "No escalation yet.")
      : h(
          "ol",
          { attrs: { "aria-label": `Escalations of ${row.incidentId}` } },
          ...row.escalations.map((step) =>
            h("li", {}, `Level ${String(step.level)} to ${step.to} at `, instantCell(step.at)),
          ),
        ),
  );
}

function routePanel(rows: readonly Route[]): HTMLElement {
  return panel(
    "Alert routes and external status",
    "incidents-routes",
    h(
      "p",
      { class: "note" },
      "If the application or this page is down, the external routes below still alert: that is how a UI outage is reported while the controller keeps managing risk.",
    ),
    h(
      "ul",
      {},
      ...rows.map((row) =>
        h(
          "li",
          { attrs: { "data-route": row.routeId, "data-independent": String(row.independent) } },
          h("strong", {}, row.name),
          ` — ${ROUTE_LABEL[row.routeKind]} `,
          badge(row.state, healthTone(row.state)),
          h("span", {}, " last heartbeat "),
          instantCell(row.lastHeartbeatAt),
          h("span", {}, "; last test "),
          row.lastTestResult === "NOT_RUN"
            ? badge("never tested", "warn")
            : h(
                "span",
                {},
                badge(row.lastTestResult, row.lastTestResult === "PASSED" ? "ok" : "bad"),
                " at ",
                instantCell(row.lastTestAt),
              ),
        ),
      ),
    ),
  );
}

/** Draw the incidents and the alert routes. */
export function drawIncidents(rows: readonly IncidentsRow[], ctx: ViewContext): readonly Child[] {
  const slot = h(
    "div",
    { class: "incident-list" },
    ...byKindOf(rows, "incident_detail").map((row) => incident(ctx, row)),
  );
  return [
    h(
      "p",
      { class: "note" },
      "Acknowledging an incident records that you saw it; it releases no safety latch. Channel delivery attempts are listed under Notifications.",
    ),
    slot,
    routePanel(byKindOf(rows, "route")),
  ];
}

/** Mount the incidents view. */
export function mountIncidents(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "incidents",
    title: "Incidents and alert routes",
    projection: "incidents",
    row: decodeIncidentsRow,
    draw: drawIncidents,
  });
}
